import {
  IDENTITY_PROVIDERS,
  SETTING_NAMES,
  SUPPORTED_CHAT_TOOLS
} from 'common/constants/constants';
import {
  teamMsteamsUnlinkedFailed,
  teamMsteamsUnlinkedSuccessfully
} from 'common/constants/messages.js';
import { redirectToMsTeams } from 'common/services/msteams.service';
import Text from 'components/base/Text';
import { Padding } from 'components/base/layout-components';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import NotConnected from '../ChatTools/NotConnected';
import { toggleNotificationsForTeam } from '../ChatTools/notifications.service';
import AuthState from '../ProjectManagement/components/AuthState';
import CompanySettings from './components/CompanySettings';
import TeamSettings from './components/TeamSettings';
import useSettingsService from 'components/hooks/useSettingsService';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useCollaborationSettings from 'components/hooks/useCollaborationSettings/useCollaborationSettings';
import { LBMsTeamsIcon } from '@linearb/buzz';

const MsTeamsSettings = ({
  team,
  msTeamsSensorAuthorization,
  uiState,
  currentTeams,
  isOrgSettings,
  selectTeamId,
  history,
  toggleSnackBar
}) => {
  const [settings, setSettings] = useState(null);
  const msteamsOrgSettings = useCollaborationSettings({
    collaborationType: SUPPORTED_CHAT_TOOLS.MSTEAMS
  });

  const { updateSetting } = useSettingsService();

  const unlinkTeam = async team => {
    try {
      const teamSettings = settings[team.id];
      await updateSetting(
        SETTING_NAMES.TEAM_MSTEAMS_CHANNEL,
        null,
        'team',
        team.id
      );
      setSettings({
        ...settings,
        [team.id]: {
          ...teamSettings,
          msTeamsChannel: null
        }
      });
      toggleSnackBar({
        message: (
          <Text size='large' color='white'>
            {`${team.name} ${teamMsteamsUnlinkedSuccessfully}`}
          </Text>
        ),
        variant: 'success'
      });
    } catch (e) {
      toggleSnackBar({
        variant: 'error',
        message: (
          <Text size='large' color='white'>
            {teamMsteamsUnlinkedFailed(team.name)}
          </Text>
        )
      });
    }
  };

  useEffect(() => {
    if (!hasTeams) {
      setSettings({});
      return;
    }
    setSettings(msteamsOrgSettings);
  }, [currentTeams]);

  const { getSettingOrDefinitionValue } = useSettingsService();

  const msTeamsAppClientId = getSettingOrDefinitionValue({
    name: SETTING_NAMES.MS_TEAMS_APP_CLIENT_ID
  });
  const isEnableMsTeamsV2 = getSettingOrDefinitionValue({
    name: FEATURE_FLAGS_NAMES.enableMsTeamsV2
  });
  const msTeamsAppVersion = isEnableMsTeamsV2
    ? IDENTITY_PROVIDERS.MSTEAMS_V2
    : IDENTITY_PROVIDERS.MSTEAMS;

  const isAuthorized = _.get(msTeamsSensorAuthorization, 'state') === 'ok';
  const msteamsAppsUrl = 'https://teams.microsoft.com/l/app/';
  const clientId = `${msteamsAppsUrl}${_.get(
    msTeamsAppClientId,
    'clientId',
    ''
  )}`;

  const hasTeams = currentTeams && currentTeams.length;

  const currentTeam =
    !isOrgSettings &&
    currentTeams &&
    currentTeams.find(t => t.id === _.get(uiState, 'selectedTeamId', null));

  const currentTeamSettings = settings
    ? settings[_.get(currentTeam, 'id', null)]
    : null;

  const msTeamsChannel = _.get(currentTeamSettings, 'msTeamsChannel', null);

  const applicationName = 'MSTEAMS';

  const toggleNotificationsForTeamWrapper = async (team, value) =>
    await toggleNotificationsForTeam(
      settings,
      setSettings,
      updateSetting,
      isOrgSettings,
      team,
      value,
      _.keys(SUPPORTED_CHAT_TOOLS)[1]
    );

  const CompanySettingsWrapper = (
    <CompanySettings
      clientId={clientId}
      settings={settings}
      uiState={uiState}
      hasTeams={hasTeams}
      currentTeam={currentTeam}
      currentTeams={currentTeams}
      msTeamsSensorAuthorization={msTeamsSensorAuthorization}
      selectTeamId={selectTeamId}
      history={history}
      unlinkTeam={unlinkTeam}
      toggleNotificationsForTeam={toggleNotificationsForTeamWrapper}
    />
  );

  const TeamSettingsWrapper = (
    <TeamSettings history={history} applicationName={applicationName} />
  );

  // @ts-ignore
  const NotConnectedWrapper = <NotConnected chatTool={applicationName} />;

  return (
    <Padding t='x-medium'>
      {/* @ts-ignore */}
      <AuthState
        sensor={msTeamsSensorAuthorization}
        icon={LBMsTeamsIcon}
        name='MS Teams'
        isOrgSettings={isOrgSettings}
        onConnect={
          msTeamsSensorAuthorization
            ? null
            : () => redirectToMsTeams(team, msTeamsAppVersion)
        }
      />
      {isAuthorized
        ? isOrgSettings
          ? CompanySettingsWrapper
          : msTeamsChannel
          ? TeamSettingsWrapper
          : NotConnectedWrapper
        : NotConnectedWrapper}
    </Padding>
  );
};

export default MsTeamsSettings;
